/* eslint-disable */
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { ImageCompress } from './image-compress';
import { DOC_ORIENTATION } from './models/DOC_ORIENTATION';
import { UploadResponse } from './models/upload-response';
import { DataUrl } from './models/data-url';

/**
 * This service is for image compression.
 *
 * We are using a local version of ngx-image-compress instead of the NPM version because the latest version (^13.1.0)
 * has Angular 13 as peer dependency, and older versions do not support setting a maxHeight or minHeight.
 *
 * TODO: ES-17262 - remove local version of ngx-image-compress when upgrading to Angular 13+.
 */
@Injectable({
  providedIn: 'root',
})
export class NgxImageCompressService {
  // render object for dynamic rendering.
  private readonly render: Renderer2;

  constructor(rendererFactory: RendererFactory2) {
    this.render = rendererFactory.createRenderer(null, null);
  }

  /**
   * Helper to evaluate the compression rate.
   *
   * @param imgString - The image in base64 string format.
   * @param image
   */
  public byteCount(image: DataUrl) {
    return ImageCompress.byteCount(image);
  }

  /**
   * Get the correct Orientation value from image tags.
   *
   * @param file
   */
  public getOrientation(file: File): Promise<DOC_ORIENTATION> {
    return ImageCompress.getOrientation(file);
  }

  /**
   * Return a promise with the new image data and image orientation
   * Nothing happen if no file have been selected.
   */
  public uploadFile(): Promise<UploadResponse> {
    return ImageCompress.uploadFile(this.render, false) as Promise<UploadResponse>;
  }

  /**
   * Return a promise with an array of image data and image orientation
   * Nothing happen if no files have been selected.
   */
  public uploadMultipleFiles(): Promise<UploadResponse[]> {
    return ImageCompress.uploadFile(this.render, true) as Promise<UploadResponse[]>;
  }

  /**
   * Return a promise with the new image data and image orientation
   * the promise will reject if no file have been selected.
   */
  public uploadFileOrReject(): Promise<UploadResponse> {
    return ImageCompress.uploadFile(this.render, false, true) as Promise<UploadResponse>;
  }

  /**
   * Return a promise with an array of image data and image orientation
   * the promise will reject if no files have been selected.
   */
  public uploadMultipleFilesOrReject(): Promise<UploadResponse[]> {
    return ImageCompress.uploadFile(this.render, true, true) as Promise<UploadResponse[]>;
  }

  /**
   * Perform a compression from the given DataUrl (string), provided by the uploadFile, or uploadMultipleFiles method.
   *
   *
   * | Parameter   | Type   | Description                                                                       |
   * | ----------- | ------ | --------------------------------------------------------------------------------- |
   * | image       | string | DataUrl (string) representing the image                                           |
   * | orientation | number | EXIF Orientation value using the DOC_ORIENTATION enum value                       |
   * | ratio       | number | Maximum scale factor as a percentage (optional, default: 50) <sup>[1](#fn1)</sup> |
   * | quality     | number | JPEG quality factor as a percentage (optional, default: 50) <sup>[2](#fn2)</sup>  |
   * | maxwidth    | number | Maximum width in pixels if you need to resize (optional, default: 0 - no resize)  |
   * | maxheight   | number | Maximum height in pixels if you need to resize (optional, default: 0 - no resize) |
   *
   * @param image
   * @param orientation
   * @param ratio
   * @param quality
   * @param maxWidth
   * @param maxHeight
   */
  public compressFile(
    image: DataUrl,
    orientation: DOC_ORIENTATION,
    ratio = 50,
    quality = 50,
    maxWidth = 0,
    maxHeight = 0,
  ): Promise<DataUrl> {
    return ImageCompress.compress(image, orientation, this.render, ratio, quality, maxWidth, maxHeight);
  }

  /**
   * Most simple function to use here.
   * Perform an upload and return an image dataUrl (string format) with a maximum size, given in *MegaBytes*
   * If the size can't be reached, the best that can be reached will be returned in promise *rejection*
   * Put debugMode to true if you have some trouble to print some help using console.debug.
   *
   * @param maxSizeMb
   * @param debugMode
   */
  public uploadAndGetImageWithMaxSize(maxSizeMb = 1): Promise<DataUrl> {
    return ImageCompress.getImageMaxSize(maxSizeMb, this.render);
  }
}
